/**
 * Authenticated Fetch Utility
 *
 * Wrapper around fetch that automatically includes the JWT ID token from AWS Cognito.
 * Uses the ID token (not the access token) so the backend receives custom attributes
 * such as custom:isDemo for demo vs non-demo data routing.
 */

import { refreshSession } from '../auth/cognitoAuth';
import { getOrganizationIdFromToken } from '../auth/cognitoAuth';
import { emitSessionExpired } from '../auth/sessionEvents';

/** Local Control Centre / Athena fan-outs often need longer than a browser default wait. Prod unchanged (no client abort). */
const DEFAULT_TIMEOUT_MS = import.meta.env.DEV ? 300_000 : 0;

/**
 * Makes an authenticated fetch request with JWT token (ID token for custom claims)
 * @param timeoutMs - Abort after this many ms. Default 5m in Vite DEV, disabled in production.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> => {
  const controller = timeoutMs > 0 && !options.signal ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(
        () =>
          controller.abort(
            new DOMException(`Request timed out after ${timeoutMs}ms: ${url}`, 'TimeoutError'),
          ),
        timeoutMs,
      )
    : null;

  try {
    console.log(`[authenticatedFetch] 🔄 Starting authentication for: ${url}`);
    const tokens = await refreshSession();

    console.log(`[authenticatedFetch] refreshSession returned:`, {
      hasTokens: !!tokens,
      hasAccessToken: !!tokens?.accessToken,
      hasIdToken: !!tokens?.idToken,
      hasRefreshToken: !!tokens?.refreshToken,
    });

    if (!tokens || !tokens.idToken) {
      // Not necessarily a dead session — refreshSession() can transiently
      // return null (e.g. a background request racing Cognito's local
      // hydration). Only a 401 the backend actually returns for a token we
      // sent is treated as confirmed session expiry (see below).
      console.error('[authenticatedFetch] ❌ No valid session found. User may need to log in again.');
      throw new Error('Not authenticated. Please log in again.');
    }

    const headers = new Headers(options.headers);

    // Use ID token so backend gets custom attributes (e.g. custom:isDemo, custom:organisationId)
    headers.set('Authorization', `Bearer ${tokens.idToken}`);
    
    console.log(`[authenticatedFetch] ✅ JWT token added to Authorization header`);
    
    // Organization ID should come from the JWT token claims (custom:organisationId)
    // Don't pass it as a header - let the backend extract it from the JWT
    const orgId = getOrganizationIdFromToken(tokens.idToken);
    if (orgId) {
      console.log(`[authenticatedFetch] JWT contains organization ID: ${orgId}`);
    } else {
      console.warn('[authenticatedFetch] No organization ID in JWT token — backend will extract from JWT claim');
    }
    
    console.log(`[authenticatedFetch] Making authenticated request to: ${url}`, {
      hasToken: !!tokens.idToken,
      tokenPreview: tokens.idToken ? `${tokens.idToken.substring(0, 20)}...` : 'none',
      hasOrgId: !!orgId,
      headerKeys: Array.from(headers.keys())
    });
    
    const response = await fetch(url, {
      ...options,
      headers,
      signal: options.signal ?? controller?.signal,
    });

    console.log(`[authenticatedFetch] Response status: ${response.status} for ${url}`);

    // If unauthorized, tokens might be expired
    if (response.status === 401) {
      const errorBody = await response.text();
      let errorMessage = 'Authentication expired. Please login again.';
      
      try {
        const errorJson = JSON.parse(errorBody);
        errorMessage = errorJson.message || errorJson.error || errorMessage;
      } catch {
        // If response is not JSON, use default message
      }
      
      console.error(`[authenticatedFetch] 401 Unauthorized: ${errorMessage}`);
      // A 401 after we just attached a freshly-(re)validated token means the
      // session itself is no longer accepted server-side (e.g. refresh token
      // revoked/expired) — nothing left to retry, so send the user to login.
      emitSessionExpired();
      throw new Error(errorMessage);
    }

    return response;
  } catch (error) {
    // Re-throw with more context if it's already an Error
    if (error instanceof Error) {
      console.error(`[authenticatedFetch] Error: ${error.message}`);
      throw error;
    }
    // Wrap other errors
    throw new Error(`Failed to make authenticated request: ${error}`);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

/**
 * Helper to open API docs in a new window with authentication
 */
export const openAuthenticatedApiDocs = async () => {
  try {
    const tokens = await refreshSession();
    
    if (!tokens) {
      alert('Please log in to access API documentation');
      return;
    }
    
    // Open API docs with JWT token
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const docsUrl = `${apiBaseUrl}/api-docs?token=${encodeURIComponent(tokens.accessToken)}`;
    
    window.open(docsUrl, '_blank');
  } catch (error) {
    console.error('Failed to open API docs:', error);
    alert('Failed to authenticate for API documentation');
  }
};

export default authenticatedFetch;
